import { combineReducers } from "redux";
import { ActionSchema } from "../actions";
import { AppFeatureSchema } from "../actions/authActions";
import {
  PlatformConfigurationConst,
  GetApiKeySchema,
  GetGlobalEmailConfigSchema,
  SaveGlobalEmailConfigSchema,
  GetSMSSettingsAndConfigSchema,
  GetSMSTemplatesSchema,
  GetSMSProviderConfigSchema,
  SaveSMSConfigSchema,
  ResetSMSConfigSchema,
  GetGlobalSMSConfigSchema,
  SaveGlobalSMSConfigSchema,
  SaveSMSSettingsSchema,
  GetRolesSchema,
  GetConfigurationListSchema,
  TransferConfigurationsSchema,
  GetSocialProviderListSchema,
  GetMultipleSocialProviderSchema,
  GetAuthServerConfigApiSchema,
  GetCustomSMSProviderConfigSchema,
  SaveCustomSMSConfigSchema,
  GetOAuthProvidersSchema,
  GetJWTProvidersSchema,
  GetSAMLProvidersSchema,
  GetOIDCandOAuthAppsSchema,
  GetSSOConnectionSchema,
  GetDataMappingsSchema,
  GetCustomProviderDataMappingsSchema,
  DeleteOIDCandOAuthAppsResponseSchema,
  PutOIDCandOAuthAppsResponseSchema,
  DeleteOIDCAppsResponseSchema,
  PutOIDCAppsResponseSchema,
  GetOIDCAppsSchema,
  GetSocialProviderOptionSchema,
  GetSocialProvidersSchema,
  SocialProviderOrderResponse,
  GetSAMLAppsSchema,
  HostedPluginMutationResponseSchema,
  DeleteHostedPluginResponseSchema,
  GetJWTAppsSchema,
  PutOIDCandOAuthAppsResponse,
  GetHostedPluginAppsSchema
} from "../actions/platformConfigurationActions";
import { OptionalCaptcha } from "../actions/deploymentActions";
import { GetCustomObjectsSchema } from "../actions/customerManagementActions";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../services/analytics/tracking";

const mutationLoadingState = {
  isLoading: true,
  isSuccess: false,
  isError: false,
  error: ""
};

const withRequestTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...mutationLoadingState,
  ...getRequestTrackingState(action as any)
});

const withSuccessTracking = <T extends object>(state: T, action: ActionSchema<any>) => ({
  ...state,
  ...action.payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "success")
});

const withErrorTracking = <T extends object>(state: T, action: ActionSchema<any>, payload = action.payload) => ({
  ...state,
  ...payload,
  ...getOutcomeTrackingState(state as MutationTrackingState, "error")
});

const getMultipleSocialProviderReducer = (state = {}, action: ActionSchema<GetMultipleSocialProviderSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_MULTIPLE_SOCIAL_PROVIDER:
    case PlatformConfigurationConst.PUT_MULTIPLE_SOCIAL_PROVIDER:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.GET_MULTIPLE_SOCIAL_PROVIDER_LOADING:
    case PlatformConfigurationConst.GET_MULTIPLE_SOCIAL_PROVIDER_SUCCESS:
    case PlatformConfigurationConst.GET_MULTIPLE_SOCIAL_PROVIDER_ERROR:
      return {
        ...state,
        ...action.payload
      };
    case PlatformConfigurationConst.PUT_MULTIPLE_SOCIAL_PROVIDER_LOADING:
    case PlatformConfigurationConst.POST_MULTIPLE_SOCIAL_PROVIDER_LOADING:
      return {
        ...state,
        ...action.payload
      };
    case PlatformConfigurationConst.PUT_MULTIPLE_SOCIAL_PROVIDER_SUCCESS:
    case PlatformConfigurationConst.POST_MULTIPLE_SOCIAL_PROVIDER_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_MULTIPLE_SOCIAL_PROVIDER_ERROR:
    case PlatformConfigurationConst.PUT_MULTIPLE_SOCIAL_PROVIDER_ERROR: {
      const response = {};
      if (action.payload.isError) {
        response["isError"] = action.payload.isError;
        response["isLoading"] = action.payload.isLoading;
        response["isSuccess"] = action.payload.isSuccess;
        response["error"] = action.payload.error;
        response["response"] = state["response"];
        response["updateMethodProp"] = state["updateMethodProp"];
      }
      return withErrorTracking(state, action, response);
    }
    default:
      return state;
  }
};
// const postMultipleSocialProviderReducer = (
//   state = {},
//   action: ActionSchema<GetMultipleSocialProviderSchema>
// ) => {
//   switch (action.type) {
//     case PlatformConfigurationConst.POST_MULTIPLE_SOCIAL_PROVIDER_LOADING:
//     case PlatformConfigurationConst.POST_MULTIPLE_SOCIAL_PROVIDER_ERROR:
//     case PlatformConfigurationConst.POST_MULTIPLE_SOCIAL_PROVIDER_SUCCESS:
//       return {
//         ...state,
//         ...action.payload
//       };
//     default:
//       return state;
//   }
// };
const deleteMultipleSocialProviderReducer = (state = {}, action: ActionSchema<GetMultipleSocialProviderSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_MULTIPLE_SOCIAL_PROVIDER:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_MULTIPLE_SOCIAL_PROVIDER_LOADING:
      return {
        ...state,
        ...action.payload
      };
    case PlatformConfigurationConst.DELETE_MULTIPLE_SOCIAL_PROVIDER_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.DELETE_MULTIPLE_SOCIAL_PROVIDER_SUCCESS:
      return withSuccessTracking(state, action);
    default:
      return state;
  }
};
const getGlobalEmailConfigReducer = (state = {}, action: ActionSchema<GetGlobalEmailConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_GLOBAL_EMAIL_CONFIG_LOADING:
    case PlatformConfigurationConst.GET_GLOBAL_EMAIL_CONFIG_ERROR:
    case PlatformConfigurationConst.GET_GLOBAL_EMAIL_CONFIG_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const getSMSSettingsAndConfigReducer = (state = {}, action: ActionSchema<GetSMSSettingsAndConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SMS_SETTINGS_AND_CONFIG_SUCCESS:
    case PlatformConfigurationConst.GET_SMS_SETTINGS_AND_CONFIG_LOADING:
    case PlatformConfigurationConst.GET_SMS_SETTINGS_AND_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSMSTemplatesReducer = (state = {}, action: ActionSchema<GetSMSTemplatesSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SMS_TEMPLATES_SUCCESS:
    case PlatformConfigurationConst.GET_SMS_TEMPLATES_LOADING:
    case PlatformConfigurationConst.GET_SMS_TEMPLATES_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSMSProviderConfigReducer = (state = {}, action: ActionSchema<GetSMSProviderConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SMS_PROVIDER_CONFIG_SUCCESS:
    case PlatformConfigurationConst.GET_SMS_PROVIDER_CONFIG_LOADING:
    case PlatformConfigurationConst.GET_SMS_PROVIDER_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getCustomSMSProviderConfigReducer = (state = {}, action: ActionSchema<GetCustomSMSProviderConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_CUSTOM_SMS_PROVIDER_CONFIG_SUCCESS:
    case PlatformConfigurationConst.GET_CUSTOM_SMS_PROVIDER_CONFIG_LOADING:
    case PlatformConfigurationConst.GET_CUSTOM_SMS_PROVIDER_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const saveGlobalEmailConfigReducer = (state = {}, action: ActionSchema<SaveGlobalEmailConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SAVE_GLOBAL_EMAIL_CONFIG_LOADING:
    case PlatformConfigurationConst.SAVE_GLOBAL_EMAIL_CONFIG_ERROR:
    case PlatformConfigurationConst.SAVE_GLOBAL_EMAIL_CONFIG_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const saveSMSConfigReducer = (state = {}, action: ActionSchema<SaveSMSConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SAVE_SMS_CONFIG_SUCCESS:
    case PlatformConfigurationConst.SAVE_SMS_CONFIG_LOADING:
    case PlatformConfigurationConst.SAVE_SMS_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const saveCustomSMSConfigReducer = (state = {}, action: ActionSchema<SaveCustomSMSConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SAVE_CUSTOM_SMS_CONFIG_SUCCESS:
    case PlatformConfigurationConst.SAVE_CUSTOM_SMS_CONFIG_LOADING:
    case PlatformConfigurationConst.SAVE_CUSTOM_SMS_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const resetSMSConfigReducer = (state = {}, action: ActionSchema<ResetSMSConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.RESET_SMS_CONFIG_SUCCESS:
    case PlatformConfigurationConst.RESET_SMS_CONFIG_LOADING:
    case PlatformConfigurationConst.RESET_SMS_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getApiKeyReducer = (state = {}, action: ActionSchema<GetApiKeySchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_API_KEY_LOADING:
    case PlatformConfigurationConst.GET_API_KEY_ERROR:
    case PlatformConfigurationConst.GET_API_KEY_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const getGlobalSMSSettingsReducer = (state = {}, action: ActionSchema<GetGlobalSMSConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_GLOBAL_SMS_SETTINGS_LOADING:
    case PlatformConfigurationConst.GET_GLOBAL_SMS_SETTINGS_ERROR:
    case PlatformConfigurationConst.GET_GLOBAL_SMS_SETTINGS_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const saveGlobalSMSSettingsReducer = (state = {}, action: ActionSchema<SaveGlobalSMSConfigSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SAVE_GLOBAL_SMS_SETTINGS_LOADING:
    case PlatformConfigurationConst.SAVE_GLOBAL_SMS_SETTINGS_ERROR:
    case PlatformConfigurationConst.SAVE_GLOBAL_SMS_SETTINGS_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const saveSMSSettingsReducer = (state = {}, action: ActionSchema<SaveSMSSettingsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SAVE_SMS_SETTINGS_LOADING:
    case PlatformConfigurationConst.SAVE_SMS_SETTINGS_ERROR:
    case PlatformConfigurationConst.SAVE_SMS_SETTINGS_SUCCESS:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const getRolesReducer = (state = {}, action: ActionSchema<GetRolesSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_ROLES_LOADING:
    case PlatformConfigurationConst.GET_ROLES_SUCCESS:
    case PlatformConfigurationConst.GET_ROLES_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postRoleAndPermissionResponseReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_ROLES_AND_PERMISSION_LOADING:
    case PlatformConfigurationConst.POST_ROLES_AND_PERMISSION_SUCCESS:
    case PlatformConfigurationConst.POST_ROLES_AND_PERMISSION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const putRoleAndPermissionResponseReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.PUT_ROLES_AND_PERMISSION_LOADING:
    case PlatformConfigurationConst.PUT_ROLES_AND_PERMISSION_SUCCESS:
    case PlatformConfigurationConst.PUT_ROLES_AND_PERMISSION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const deleteRolesAndPermissionResponseReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_ROLES_AND_PERMISSION_LOADING:
    case PlatformConfigurationConst.DELETE_ROLES_AND_PERMISSION_SUCCESS:
    case PlatformConfigurationConst.DELETE_ROLES_AND_PERMISSION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deletePermissionOnlyResponseReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_PERMISSION_ONLY_LOADING:
    case PlatformConfigurationConst.DELETE_PERMISSION_ONLY_ERROR:
    case PlatformConfigurationConst.DELETE_PERMISSION_ONLY_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const putDefaultRoleResponseReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.PUT_DEFAULT_ROLE_LOADING:
    case PlatformConfigurationConst.GET_DEFAULT_ROLE_LOADING:
    case PlatformConfigurationConst.PUT_DEFAULT_ROLE_ERROR:
    case PlatformConfigurationConst.PUT_DEFAULT_ROLE_SUCCESS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getConfigurationListReducer = (state = {}, action: ActionSchema<GetConfigurationListSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_CONFIGURATION_LIST_LOADING:
    case PlatformConfigurationConst.GET_CONFIGURATION_LIST_SUCCESS:
    case PlatformConfigurationConst.GET_CONFIGURATION_LIST_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSocialProviderListReducer = (state = {}, action: ActionSchema<GetSocialProviderListSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SOCIALPROVIDER_LIST_LOADING:
    case PlatformConfigurationConst.GET_SOCIALPROVIDER_LIST_SUCCESS:
    case PlatformConfigurationConst.GET_SOCIALPROVIDER_LIST_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const transferConfigurationsReducer = (state = {}, action: ActionSchema<TransferConfigurationsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.TRANSFER_CONFIGURATIONS_LOADING:
    case PlatformConfigurationConst.TRANSFER_CONFIGURATIONS_SUCCESS:
    case PlatformConfigurationConst.TRANSFER_CONFIGURATIONS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteAuthServerConfigApiReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_AUTH_SERVER_API_CONFIG:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_AUTH_SERVER_API_CONFIG_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_AUTH_SERVER_API_CONFIG_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.DELETE_AUTH_SERVER_API_CONFIG_LOADING:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const getAuthServerConfigApiReducer = (state = {}, action: ActionSchema<GetAuthServerConfigApiSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_AUTH_SERVER_API_CONFIG_LOADING:
    case PlatformConfigurationConst.GET_AUTH_SERVER_API_CONFIG_SUCCESS:
    case PlatformConfigurationConst.GET_AUTH_SERVER_API_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const updateAuthServerConfigApiReducer = (state = {}, action: ActionSchema<GetAuthServerConfigApiSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.PUT_AUTH_SERVER_API_CONFIG:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.PUT_AUTH_SERVER_API_CONFIG_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.PUT_AUTH_SERVER_API_CONFIG_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.PUT_AUTH_SERVER_API_CONFIG_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const setAuthServerConfigApiReducer = (state = {}, action: ActionSchema<GetAuthServerConfigApiSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_AUTH_SERVER_API_CONFIG:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_AUTH_SERVER_API_CONFIG_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_AUTH_SERVER_API_CONFIG_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.POST_AUTH_SERVER_API_CONFIG_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const setAppFeaturesReducer = (state = {}, action: ActionSchema<AppFeatureSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SET_APP_FEATURES:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.SET_APP_FEATURES_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.SET_APP_FEATURES_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.SET_APP_FEATURES_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const renewCertificateReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.RENEW_CERTIFICATE_SUCCESS:
    case PlatformConfigurationConst.RENEW_CERTIFICATE_LOADING:
    case PlatformConfigurationConst.RENEW_CERTIFICATE_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getAvailableSSOIntegrationReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.AVAILABLE_SSO_INTEGRATION_SUCCESS:
    case PlatformConfigurationConst.AVAILABLE_SSO_INTEGRATION_LOADING:
    case PlatformConfigurationConst.AVAILABLE_SSO_INTEGRATION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getUserSamlIntegrationByTypeReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SAML_INTEGRATION_BY_TYPE_SUCCESS:
    case PlatformConfigurationConst.SAML_INTEGRATION_BY_TYPE_LOADING:
    case PlatformConfigurationConst.SAML_INTEGRATION_BY_TYPE_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postSSOIntegrationReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_SSO_INTEGRATION:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_SSO_INTEGRATION_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.POST_SSO_INTEGRATION_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_SSO_INTEGRATION_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const deleteSSOIntegrationReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_SSO_INTEGRATION:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_SSO_INTEGRATION_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.DELETE_SSO_INTEGRATION_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_SSO_INTEGRATION_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getOAuthProvidersReducer = (state = {}, action: ActionSchema<GetOAuthProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_OAUTH_PROVIDERS_SUCCESS:
    case PlatformConfigurationConst.GET_OAUTH_PROVIDERS_LOADING:
    case PlatformConfigurationConst.GET_OAUTH_PROVIDERS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postOAuthProvidersReducer = (state = {}, action: ActionSchema<GetOAuthProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_OAUTH_PROVIDERS_SUCCESS:
    case PlatformConfigurationConst.POST_OAUTH_PROVIDERS_LOADING:
    case PlatformConfigurationConst.POST_OAUTH_PROVIDERS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const putOAuthProvidersReducer = (state = {}, action: ActionSchema<GetOAuthProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.UPDATE_OAUTH_PROVIDERS_SUCCESS:
    case PlatformConfigurationConst.UPDATE_OAUTH_PROVIDERS_LOADING:
    case PlatformConfigurationConst.UPDATE_OAUTH_PROVIDERS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteOAuthProvidersReducer = (state = {}, action: ActionSchema<GetOAuthProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_OAUTH_PROVIDERS_SUCCESS:
    case PlatformConfigurationConst.DELETE_OAUTH_PROVIDERS_LOADING:
    case PlatformConfigurationConst.DELETE_OAUTH_PROVIDERS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getJWTProvidersReducer = (state = {}, action: ActionSchema<GetJWTProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_JWT_PROVIDERS_SUCCESS:
    case PlatformConfigurationConst.GET_JWT_PROVIDERS_LOADING:
    case PlatformConfigurationConst.GET_JWT_PROVIDERS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postJWTProvidersReducer = (state = {}, action: ActionSchema<GetJWTProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_JWT_PROVIDERS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_JWT_PROVIDERS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_JWT_PROVIDERS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.POST_JWT_PROVIDERS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const putJWTProvidersReducer = (state = {}, action: ActionSchema<GetJWTProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.UPDATE_JWT_PROVIDERS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.UPDATE_JWT_PROVIDERS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.UPDATE_JWT_PROVIDERS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.UPDATE_JWT_PROVIDERS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteJWTProvidersReducer = (state = {}, action: ActionSchema<GetJWTProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_JWT_PROVIDERS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_JWT_PROVIDERS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_JWT_PROVIDERS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.DELETE_JWT_PROVIDERS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSAMLProvidersReducer = (state = {}, action: ActionSchema<GetSAMLProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SAML_PROVIDERS_SUCCESS:
    case PlatformConfigurationConst.GET_SAML_PROVIDERS_LOADING:
    case PlatformConfigurationConst.GET_SAML_PROVIDERS_ERROR:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.SYNC_SAML_PROVIDERS:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postSAMLProvidersReducer = (state = {}, action: ActionSchema<GetSAMLProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_SAML_PROVIDERS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_SAML_PROVIDERS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_SAML_PROVIDERS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.POST_SAML_PROVIDERS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const putSAMLProvidersReducer = (state = {}, action: ActionSchema<GetSAMLProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.UPDATE_SAML_PROVIDERS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.UPDATE_SAML_PROVIDERS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.UPDATE_SAML_PROVIDERS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.UPDATE_SAML_PROVIDERS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteSAMLProvidersReducer = (state = {}, action: ActionSchema<GetSAMLProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_SAML_PROVIDERS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_SAML_PROVIDERS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_SAML_PROVIDERS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.DELETE_SAML_PROVIDERS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getOIDCandOAuthAppsReducer = (state = {}, action: ActionSchema<GetOIDCandOAuthAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_OIDC_AND_OAUTH_APPS_SUCCESS:
    case PlatformConfigurationConst.GET_OIDC_AND_OAUTH_APPS_LOADING:
    case PlatformConfigurationConst.GET_OIDC_AND_OAUTH_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSAMLAppsReducer = (state = {}, action: ActionSchema<GetSAMLAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SAML_APPS_SUCCESS:
    case PlatformConfigurationConst.GET_SAML_APPS_LOADING:
    case PlatformConfigurationConst.GET_SAML_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postSAMLAppsReducer = (state = {}, action: ActionSchema<GetSAMLAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_SAML_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_SAML_APPS_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.POST_SAML_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_SAML_APPS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const putSAMLAppsReducer = (state = {}, action: ActionSchema<GetSAMLAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.UPDATE_SAML_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.UPDATE_SAML_APPS_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.UPDATE_SAML_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.UPDATE_SAML_APPS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const deleteSAMLAppsReducer = (state = {}, action: ActionSchema<DeleteOIDCAppsResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_SAML_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_SAML_APPS_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.DELETE_SAML_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_SAML_APPS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getJWTAppsReducer = (state = {}, action: ActionSchema<GetJWTAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_JWT_APPS_SUCCESS:
    case PlatformConfigurationConst.GET_JWT_APPS_LOADING:
    case PlatformConfigurationConst.GET_JWT_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postJWTAppsReducer = (state = {}, action: ActionSchema<GetJWTAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_JWT_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_JWT_APPS_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.POST_JWT_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_JWT_APPS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const putJWTAppsReducer = (state = {}, action: ActionSchema<GetJWTAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.UPDATE_JWT_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.UPDATE_JWT_APPS_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.UPDATE_JWT_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.UPDATE_JWT_APPS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};
const deleteJWTAppsReducer = (state = {}, action: ActionSchema<DeleteOIDCAppsResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_JWT_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_JWT_APPS_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.DELETE_JWT_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_JWT_APPS_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getHostedPluginAppsReducer = (state = {}, action: ActionSchema<GetHostedPluginAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_HOSTED_PLUGIN_APPS_SUCCESS:
    case PlatformConfigurationConst.GET_HOSTED_PLUGIN_APPS_LOADING:
    case PlatformConfigurationConst.GET_HOSTED_PLUGIN_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const saveHostedPluginReducer = (state = {}, action: ActionSchema<HostedPluginMutationResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.SAVE_HOSTED_PLUGIN:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.SAVE_HOSTED_PLUGIN_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.SAVE_HOSTED_PLUGIN_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.SAVE_HOSTED_PLUGIN_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const deleteHostedPluginReducer = (state = {}, action: ActionSchema<DeleteHostedPluginResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_HOSTED_PLUGIN:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_HOSTED_PLUGIN_LOADING:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.DELETE_HOSTED_PLUGIN_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_HOSTED_PLUGIN_ERROR:
      return withErrorTracking(state, action);
    default:
      return state;
  }
};

const getSSOConnectionReducer = (state = {}, action: ActionSchema<GetSSOConnectionSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SSO_CONNECTION_SUCCESS:
    case PlatformConfigurationConst.GET_SSO_CONNECTION_LOADING:
    case PlatformConfigurationConst.GET_SSO_CONNECTION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getDataMappingReducer = (state = {}, action: ActionSchema<GetDataMappingsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_DATA_MAPPING_LOADING:
    case PlatformConfigurationConst.GET_DATA_MAPPING_SUCCESS:
    case PlatformConfigurationConst.GET_DATA_MAPPING_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getCustomProviderDataMappingReducer = (state = {}, action: ActionSchema<GetCustomProviderDataMappingsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_CUSTOM_PROVIDER_DATA_MAPPING_LOADING:
    case PlatformConfigurationConst.GET_CUSTOM_PROVIDER_DATA_MAPPING_SUCCESS:
    case PlatformConfigurationConst.GET_CUSTOM_PROVIDER_DATA_MAPPING_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postOIDCandOAuthAppsReducer = (state = {}, action: ActionSchema<GetOIDCandOAuthAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_OIDC_AND_OAUTH_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_OIDC_AND_OAUTH_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_OIDC_AND_OAUTH_APPS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.POST_OIDC_AND_OAUTH_APPS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const putOIDCandOAuthAppsReducer = (state = {}, action: ActionSchema<PutOIDCandOAuthAppsResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.PUT_OIDC_AND_OAUTH_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.PUT_OIDC_AND_OAUTH_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.PUT_OIDC_AND_OAUTH_APPS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.PUT_OIDC_AND_OAUTH_APPS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteOIDCandOAuthAppsReducer = (state = {}, action: ActionSchema<DeleteOIDCandOAuthAppsResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_OIDC_AND_OAUTH_APPS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.DELETE_OIDC_AND_OAUTH_APPS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.DELETE_OIDC_AND_OAUTH_APPS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.DELETE_OIDC_AND_OAUTH_APPS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getOIDCAppsReducer = (state = {}, action: ActionSchema<GetOIDCAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_OIDC_APPS_SUCCESS:
    case PlatformConfigurationConst.GET_OIDC_APPS_LOADING:
    case PlatformConfigurationConst.GET_OIDC_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postOIDCAppsReducer = (state = {}, action: ActionSchema<GetOIDCAppsSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_OIDC_APPS_SUCCESS:
    case PlatformConfigurationConst.POST_OIDC_APPS_LOADING:
    case PlatformConfigurationConst.POST_OIDC_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const putOIDCAppsReducer = (state = {}, action: ActionSchema<PutOIDCAppsResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.PUT_OIDC_APPS_SUCCESS:
    case PlatformConfigurationConst.PUT_OIDC_APPS_LOADING:
    case PlatformConfigurationConst.PUT_OIDC_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const deleteOIDCAppsReducer = (state = {}, action: ActionSchema<DeleteOIDCAppsResponseSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.DELETE_OIDC_APPS_SUCCESS:
    case PlatformConfigurationConst.DELETE_OIDC_APPS_LOADING:
    case PlatformConfigurationConst.DELETE_OIDC_APPS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const getOptionalCaptchaApisReducer = (state = {}, action: ActionSchema<OptionalCaptcha>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_OPTIONAL_CAPTCHA_APIS_SUCCESS:
    case PlatformConfigurationConst.GET_OPTIONAL_CAPTCHA_APIS_LOADING:
    case PlatformConfigurationConst.GET_OPTIONAL_CAPTCHA_APIS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getPassKeyConfigReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_PASSKEY_CONFIG_SUCCESS:
    case PlatformConfigurationConst.GET_PASSKEY_CONFIG_LOADING:
    case PlatformConfigurationConst.GET_PASSKEY_CONFIG_ERROR:
      return { ...state, ...action.payload };
    case PlatformConfigurationConst.SYNC_PASSKEY_CONFIG:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};
const updatePassKeyConfigReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.UPDATE_PASSKEY_CONFIG_SUCCESS:
    case PlatformConfigurationConst.UPDATE_PASSKEY_CONFIG_LOADING:
    case PlatformConfigurationConst.UPDATE_PASSKEY_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSocialProviderOptionReducer = (state = {}, action: ActionSchema<GetSocialProviderOptionSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SOCIALPROVIDER_OPTION_LOADING:
    case PlatformConfigurationConst.GET_SOCIALPROVIDER_OPTION_SUCCESS:
    case PlatformConfigurationConst.GET_SOCIALPROVIDER_OPTION_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getSocialProvidersReducer = (state = {}, action: ActionSchema<GetSocialProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.GET_SOCIAL_PROVIDERS_LOADING:
    case PlatformConfigurationConst.GET_SOCIAL_PROVIDERS_SUCCESS:
    case PlatformConfigurationConst.GET_SOCIAL_PROVIDERS_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postAppleConfigReducer = (state = {}, action: ActionSchema<any>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_APPLE_CONFIG_LOADING:
    case PlatformConfigurationConst.POST_APPLE_CONFIG_SUCCESS:
    case PlatformConfigurationConst.POST_APPLE_CONFIG_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postSocialProviderOptionReducer = (state = {}, action: ActionSchema<GetSocialProviderOptionSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_SOCIALPROVIDER_OPTION:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_SOCIALPROVIDER_OPTION_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_SOCIALPROVIDER_OPTION_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.POST_SOCIALPROVIDER_OPTION_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postSocialProvidersReducer = (state = {}, action: ActionSchema<GetSocialProvidersSchema>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_SOCIAL_PROVIDERS:
      return withRequestTracking(state, action);
    case PlatformConfigurationConst.POST_SOCIAL_PROVIDERS_SUCCESS:
      return withSuccessTracking(state, action);
    case PlatformConfigurationConst.POST_SOCIAL_PROVIDERS_ERROR:
      return withErrorTracking(state, action);
    case PlatformConfigurationConst.POST_SOCIAL_PROVIDERS_LOADING:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const postSocialProvidersOrderReducer = (state = {}, action: ActionSchema<SocialProviderOrderResponse>) => {
  switch (action.type) {
    case PlatformConfigurationConst.POST_SOCIAL_PROVIDERS_ORDER_LOADING:
    case PlatformConfigurationConst.POST_SOCIAL_PROVIDERS_ORDER_SUCCESS:
    case PlatformConfigurationConst.POST_SOCIAL_PROVIDERS_ORDER_ERROR:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

// Combine Reducers
export const platformConfigurationReducer = combineReducers({
  globalEmailConfig: getGlobalEmailConfigReducer,
  saveGlobalEmailConfigResponse: saveGlobalEmailConfigReducer,
  getApiKey: getApiKeyReducer,
  smsSettingsAndConfig: getSMSSettingsAndConfigReducer,
  smsTemplates: getSMSTemplatesReducer,
  smsProviderConfig: getSMSProviderConfigReducer,
  customSmsProviderConfig: getCustomSMSProviderConfigReducer,
  saveSMSConfigResponse: saveSMSConfigReducer,
  saveCustomSMSConfigResponse: saveCustomSMSConfigReducer,
  resetSMSConfigResponse: resetSMSConfigReducer,
  globalSMSSettings: getGlobalSMSSettingsReducer,
  saveGlobalSMSSettingsResponse: saveGlobalSMSSettingsReducer,
  saveSMSSettingsResponse: saveSMSSettingsReducer,
  getRoles: getRolesReducer,
  postRoleAndPermissionResponse: postRoleAndPermissionResponseReducer,
  putRolesAndPermissionResponse: putRoleAndPermissionResponseReducer,
  deleteRolesAndPermissionResponse: deleteRolesAndPermissionResponseReducer,
  deletePermissionOnlyResponse: deletePermissionOnlyResponseReducer,
  putDefaultRoleResponse: putDefaultRoleResponseReducer,
  getConfigurationList: getConfigurationListReducer,
  getSocialProviderList: getSocialProviderListReducer,
  transferConfigurations: transferConfigurationsReducer,
  getMultipleSocialProvider: getMultipleSocialProviderReducer,
  // postMultipleSocialProvider: postMultipleSocialProviderReducer,
  deleteMultipleSocialProvider: deleteMultipleSocialProviderReducer,
  getAuthServerConfigApi: getAuthServerConfigApiReducer,
  deleteAuthServerConfigApi: deleteAuthServerConfigApiReducer,
  updateAuthServerConfigAPi: updateAuthServerConfigApiReducer,
  setAuthServerConfigApi: setAuthServerConfigApiReducer,
  setAppFeatures: setAppFeaturesReducer,
  getRenewCertificateData: renewCertificateReducer,
  getAvailableSSOIntegrationData: getAvailableSSOIntegrationReducer,
  getUserSamlIntegration: getUserSamlIntegrationByTypeReducer,
  saveSSOIntegrationData: postSSOIntegrationReducer,
  deleteSSOIntegrationData: deleteSSOIntegrationReducer,
  getOAuthProviders: getOAuthProvidersReducer,
  postOAuthProviders: postOAuthProvidersReducer,
  putOAuthProviders: putOAuthProvidersReducer,
  deleteOAuthProviders: deleteOAuthProvidersReducer,

  getJWTProviders: getJWTProvidersReducer,
  postJWTProviders: postJWTProvidersReducer,
  putJWTProviders: putJWTProvidersReducer,
  deleteJWTProviders: deleteJWTProvidersReducer,

  getSAMLProviders: getSAMLProvidersReducer,
  postSAMLProviders: postSAMLProvidersReducer,
  putSAMLProviders: putSAMLProvidersReducer,
  deleteSAMLProviders: deleteSAMLProvidersReducer,

  getOIDCandOAuthApps: getOIDCandOAuthAppsReducer,
  getSAMLApps: getSAMLAppsReducer,
  postSAMLApps: postSAMLAppsReducer,
  putSAMLApps: putSAMLAppsReducer,
  deleteSAMLApps: deleteSAMLAppsReducer,
  getJWTApps: getJWTAppsReducer,
  postJWTApps: postJWTAppsReducer,
  putJWTApps: putJWTAppsReducer,
  deleteJWTApps: deleteJWTAppsReducer,
  getHostedPluginApps: getHostedPluginAppsReducer,
  saveHostedPlugin: saveHostedPluginReducer,
  deleteHostedPlugin: deleteHostedPluginReducer,
  getSSOConnections: getSSOConnectionReducer,
  getDataMapping: getDataMappingReducer,
  customProviderDataMapping: getCustomProviderDataMappingReducer,
  saveOIDCandOAuthAppsData: postOIDCandOAuthAppsReducer,
  updateOIDCandOAuthAppsData: putOIDCandOAuthAppsReducer,
  deleteOIDCandOAuthAppsData: deleteOIDCandOAuthAppsReducer,
  getOIDCApps: getOIDCAppsReducer,
  saveOIDCAppsData: postOIDCAppsReducer,
  updateOIDCAppsData: putOIDCAppsReducer,
  deleteOIDCAppsData: deleteOIDCAppsReducer,
  getOptionalCaptchaApis: getOptionalCaptchaApisReducer,
  getPassKeyConfig: getPassKeyConfigReducer,
  updatePassKeyConfig: updatePassKeyConfigReducer,
  getSocialProviderOptionData: getSocialProviderOptionReducer,
  getSocialProvidersData: getSocialProvidersReducer,
  postAppleConfigData: postAppleConfigReducer,
  postSocialProviderOptionData: postSocialProviderOptionReducer,
  postSocialProvidersData: postSocialProvidersReducer,
  postSocialProvidersOrderData: postSocialProvidersOrderReducer
});

// Combine schemas
export interface PlatformConfigurationSchema {
  globalEmailConfig: GetGlobalEmailConfigSchema;
  saveGlobalEmailConfigResponse: SaveGlobalEmailConfigSchema;
  getApiKey: GetApiKeySchema;
  smsSettingsAndConfig: GetSMSSettingsAndConfigSchema;
  smsTemplates: GetSMSTemplatesSchema;
  smsProviderConfig: GetSMSProviderConfigSchema;
  customSmsProviderConfig: GetCustomSMSProviderConfigSchema;
  saveSMSConfigResponse: SaveSMSConfigSchema;
  saveCustomSMSConfigResponse: SaveCustomSMSConfigSchema;
  resetSMSConfigResponse: ResetSMSConfigSchema;
  globalSMSSettings: GetGlobalSMSConfigSchema;
  saveGlobalSMSSettingsResponse: SaveGlobalSMSConfigSchema;
  saveSMSSettingsResponse: SaveSMSSettingsSchema;
  getRoles: GetRolesSchema;
  postRoleAndPermissionResponse: any;
  putRolesAndPermissionResponse: any;
  deleteRolesAndPermissionResponse: any;
  deletePermissionOnlyResponse: any;
  putDefaultRoleResponse: any;

  getConfigurationList: GetConfigurationListSchema;
  getSocialProviderList: GetSocialProviderListSchema;
  transferConfigurations: TransferConfigurationsSchema;
  getMultipleSocialProvider: GetMultipleSocialProviderSchema;
  // postMultipleSocialProvider: GetMultipleSocialProviderSchema;
  deleteMultipleSocialProvider: any;
  getAuthServerConfigApi: GetAuthServerConfigApiSchema;
  deleteAuthServerConfigApi: GetAuthServerConfigApiSchema;
  updateAuthServerConfigAPi: GetAuthServerConfigApiSchema;
  setAuthServerConfigApi: GetAuthServerConfigApiSchema;
  setAppFeatures: AppFeatureSchema;
  getRenewCertificateData: any;
  getAvailableSSOIntegrationData: any;
  getUserSamlIntegration: any;
  saveSSOIntegrationData: any;
  deleteSSOIntegrationData: any;
  getOAuthProviders: GetOAuthProvidersSchema;
  postOAuthProviders: any;
  putOAuthProviders: any;
  deleteOAuthProviders: any;

  getJWTProviders: GetJWTProvidersSchema;
  postJWTProviders: any;
  putJWTProviders: any;
  deleteJWTProviders: any;

  getSAMLProviders: GetSAMLProvidersSchema;
  postSAMLProviders: any;
  putSAMLProviders: any;
  deleteSAMLProviders: any;

  getCustomObjects: GetCustomObjectsSchema;
  getOIDCandOAuthApps: GetOIDCandOAuthAppsSchema;
  getSAMLApps: GetSAMLAppsSchema;
  postSAMLApps: GetSAMLAppsSchema;
  putSAMLApps: GetSAMLAppsSchema;
  deleteSAMLApps: any;
  getJWTApps: GetJWTAppsSchema;
  postJWTApps: GetJWTAppsSchema;
  putJWTApps: GetJWTAppsSchema;
  deleteJWTApps: any;
  getHostedPluginApps: GetHostedPluginAppsSchema;
  saveHostedPlugin: HostedPluginMutationResponseSchema;
  deleteHostedPlugin: DeleteHostedPluginResponseSchema;

  getSSOConnections: GetSSOConnectionSchema;
  getDataMapping: GetDataMappingsSchema;
  customProviderDataMapping: GetCustomProviderDataMappingsSchema;
  saveOIDCandOAuthAppsData: PutOIDCandOAuthAppsResponse;
  updateOIDCandOAuthAppsData: PutOIDCandOAuthAppsResponse;
  deleteOIDCandOAuthAppsData: DeleteOIDCAppsResponseSchema;
  getOIDCApps: GetOIDCAppsSchema;
  saveOIDCAppsData: GetOIDCAppsSchema;
  updateOIDCAppsData: PutOIDCAppsResponseSchema;
  deleteOIDCAppsData: DeleteOIDCAppsResponseSchema;
  getOptionalCaptchaApis: OptionalCaptcha;
  getPassKeyConfig: any;
  updatePassKeyConfig: any;
  getSocialProviderOptionData: GetSocialProviderOptionSchema;
  getSocialProvidersData: GetSocialProvidersSchema;
  postAppleConfigData: any;
  postSocialProviderOptionData: GetSocialProviderOptionSchema;
  postSocialProvidersData: GetSocialProvidersSchema;
  postSocialProvidersOrderData: any;
}

export default platformConfigurationReducer;
